import React from 'react';
import { X, AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * A beautiful, reusable confirmation dialog matching the Yaseero design language.
 */
const ConfirmDialog = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title = 'Are you sure?', 
    message = 'This action cannot be undone.', 
    confirmText = 'Confirm', 
    cancelText = 'Cancel',
    type = 'info', // 'info', 'warning', 'danger', 'success'
    loading = false 
}) => {
    if (!isOpen) return null;

    const getTypeStyles = () => {
        switch (type) {
            case 'danger':
                return {
                    icon: <AlertCircle className="text-rose-500" size={24} />,
                    btn: 'bg-rose-500 hover:bg-rose-600 shadow-rose-200',
                    bg: 'bg-rose-50'
                };
            case 'success':
                return {
                    icon: <CheckCircle2 className="text-emerald-500" size={24} />,
                    btn: 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-200',
                    bg: 'bg-emerald-50'
                };
            case 'warning':
                return {
                    icon: <AlertCircle className="text-amber-500" size={24} />,
                    btn: 'bg-amber-500 hover:bg-amber-600 shadow-amber-200',
                    bg: 'bg-amber-50'
                };
            default:
                return {
                    icon: <AlertCircle className="text-blue-500" size={24} />,
                    btn: 'bg-blue-600 hover:bg-blue-700 shadow-blue-200',
                    bg: 'bg-blue-50'
                };
        }
    };

    const styles = getTypeStyles();

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100">
                {/* Header */}
                <div className="p-6 flex flex-col items-center text-center">
                    <div className={`w-14 h-14 rounded-2xl ${styles.bg} flex items-center justify-center mb-4 ring-8 ring-white shadow-inner`}>
                        {styles.icon}
                    </div>
                    <h3 className="text-xl font-black text-gray-900 leading-tight mb-2 tracking-tight">
                        {title}
                    </h3>
                    <p className="text-sm text-gray-500 font-medium px-4">
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div className="p-4 bg-gray-50 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 py-3 px-4 rounded-xl text-gray-600 font-bold text-sm hover:bg-gray-200 transition-all active:scale-95 disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 py-3 px-4 rounded-xl text-white font-bold text-sm transition-all active:scale-95 disabled:opacity-50 shadow-lg ${styles.btn}`}
                    >
                        {loading ? 'Processing...' : confirmText}
                    </button>
                </div>

                {/* Close Button (top-right) */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X size={20} />
                </button>
            </div>
        </div>
    );
};

export default ConfirmDialog;
